import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface FinancialSummaryProps {
  currency: string;
  originalInvoiceTotal: number;
  totalCredited: number;
  totalDebited: number;
  remainingBalance: number;
  className?: string;
}

function formatAmount(currency: string, amount: number): string {
  return `${currency} ${Number(amount).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function FinancialSummary({
  currency,
  originalInvoiceTotal,
  totalCredited,
  totalDebited,
  remainingBalance,
  className,
}: FinancialSummaryProps): JSX.Element {
  const { t } = useTranslation();

  const remainingClass =
    remainingBalance > 0
      ? "text-green-700 dark:text-green-400 font-semibold"
      : remainingBalance < 0
        ? "text-destructive font-semibold"
        : "text-muted-foreground";

  const isFullySettled = remainingBalance === 0;
  const hasCreditOrDebitNotes = totalCredited !== 0 || totalDebited !== 0;

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t("invoices.detail.accountingSummary.title", "Accounting Summary")}
        </CardTitle>
        {hasCreditOrDebitNotes && (
          <p className="text-sm text-muted-foreground font-normal mt-1">
            {t(
              "invoices.detail.accountingSummary.helper",
              "This summary shows how this invoice's balance is affected by credit and debit notes."
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70%]">
                {t("invoices.detail.financialSummary.concept", "Concept")}
              </TableHead>
              <TableHead className="text-right">
                {t("invoices.detail.financialSummary.amount", "Amount")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("invoices.detail.financialSummary.originalTotal", "Original invoice total")}
              </TableCell>
              <TableCell className="text-right">
                {formatAmount(currency, originalInvoiceTotal)}
              </TableCell>
            </TableRow>
            {hasCreditOrDebitNotes && (
              <>
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {t(
                      "invoices.detail.financialSummary.totalCredited",
                      "Total credited (Notas de Crédito)"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatAmount(currency, totalCredited)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {t(
                      "invoices.detail.financialSummary.totalDebited",
                      "Total debited (Notas de Débito)"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatAmount(currency, totalDebited)}
                  </TableCell>
                </TableRow>
              </>
            )}
            <TableRow className={cn("bg-muted/50", isFullySettled && "border-muted")}>
              <TableCell className="font-semibold text-foreground">
                {t("invoices.detail.financialSummary.remainingBalance", "Remaining balance")}
              </TableCell>
              <TableCell className={cn("text-right font-semibold", remainingClass)}>
                <span className="inline-flex items-center gap-2">
                  {formatAmount(currency, remainingBalance)}
                  {isFullySettled && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {t("invoices.detail.accountingSummary.fullySettled", "Fully settled")}
                    </span>
                  )}
                </span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {hasCreditOrDebitNotes && (
          <>
            <p className="text-xs text-muted-foreground">
              {t(
                "invoices.detail.accountingSummary.helperCredit",
                "Credit Notes reduce the balance."
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(
                "invoices.detail.accountingSummary.helperDebit",
                "Debit Notes increase the balance."
              )}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
